import React, { useState, useEffect, useMemo } from 'react';
import { spettanzeAPI, mediciAPI } from '../services/api';
import '../styles/Spettanze.css';

const Spettanze = () => {
  // Default: mese corrente
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
    .toISOString()
    .split('T')[0];
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    .toISOString()
    .split('T')[0];

  const [dal, setDal] = useState(firstDay);
  const [al, setAl] = useState(lastDay);
  const [medicoId, setMedicoId] = useState('');
  const [medici, setMedici] = useState([]);

  const [riepilogo, setRiepilogo] = useState(null);
  const [dettaglio, setDettaglio] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedMedico, setExpandedMedico] = useState(null);

  // Carica lista medici per il filtro
  useEffect(() => {
    const loadMedici = async () => {
      try {
        const response = await mediciAPI.getAll(true);
        if (response.data.success) {
          setMedici(response.data.data.medici || []);
        }
      } catch (err) {
        console.error('Errore caricamento medici:', err);
      }
    };
    loadMedici();
  }, []);

  // Carica dati al cambio dei filtri
  useEffect(() => {
    loadData();
  }, [dal, al, medicoId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { dal, al };
      if (medicoId) params.medico_id = medicoId;

      const [riepResp, detResp] = await Promise.all([
        spettanzeAPI.getRiepilogo(params),
        spettanzeAPI.getDettaglio(params),
      ]);

      if (riepResp.data.success) setRiepilogo(riepResp.data.data);
      if (detResp.data.success) setDettaglio(detResp.data.data.visite || []);
    } catch (err) {
      console.error('Errore caricamento spettanze:', err);
      setError('Errore nel caricamento delle spettanze');
    } finally {
      setLoading(false);
    }
  };

  // Scorciatoie periodi
  const setMeseCorrente = () => {
    const now = new Date();
    setDal(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]);
    setAl(new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0]);
  };

  const setMesePrecedente = () => {
    const now = new Date();
    setDal(new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0]);
    setAl(new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0]);
  };

  const setAnnoCorrente = () => {
    const now = new Date();
    setDal(new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0]);
    setAl(new Date(now.getFullYear(), 11, 31).toISOString().split('T')[0]);
  };

  const formatEuro = (v) => {
    const n = parseFloat(v);
    if (isNaN(n)) return '€ 0,00';
    return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('it-IT');
  };

  // Dettaglio raggruppato per medico
  const dettaglioPerMedico = useMemo(() => {
    const map = {};
    for (const v of dettaglio) {
      if (!map[v.medico_id]) map[v.medico_id] = [];
      map[v.medico_id].push(v);
    }
    return map;
  }, [dettaglio]);

  const toggleExpand = (mid) => {
    setExpandedMedico((prev) => (prev === mid ? null : mid));
  };

  const exportCSV = () => {
    if (!riepilogo || !riepilogo.medici || riepilogo.medici.length === 0) return;

    const header = [
      'Medico',
      'Specializzazione',
      'N° Visite',
      'N° Prestazioni',
      'Imponibile',
      'Costi prodotto',
      'Margine',
      '% Spettanza',
      'Spettanza',
      'Ritenuta',
      'Netto',
    ];

    const rows = riepilogo.medici.map((m) => [
      `Dr. ${m.cognome} ${m.nome}`,
      m.specializzazione || '',
      m.prestazioni,
      m.prestazioni,
      parseFloat(m.imponibile).toFixed(2).replace('.', ','),
      parseFloat(m.costi_prodotto).toFixed(2).replace('.', ','),
      parseFloat(m.margine).toFixed(2).replace('.', ','),
      parseFloat(m.percentuale_spettanza).toFixed(2).replace('.', ','),
      parseFloat(m.spettanza).toFixed(2).replace('.', ','),
      parseFloat(m.ritenuta || 0).toFixed(2).replace('.', ','),
      parseFloat(m.netto || 0).toFixed(2).replace('.', ','),
    ]);

    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `spettanze_${dal}_${al}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="spettanze-container">
      <div className="spettanze-header">
        <h2>Calcolo spettanze</h2>
        <button
          className="btn-primary"
          onClick={exportCSV}
          disabled={!riepilogo || !riepilogo.medici || riepilogo.medici.length === 0}
        >
          Esporta CSV
        </button>
      </div>

      {/* Filtri */}
      <div className="spettanze-filtri" data-non-trattiene>
        <div className="filtri-row">
          <div className="filtro-group">
            <label>Dal</label>
            <input type="date" value={dal} onChange={(e) => setDal(e.target.value)} />
          </div>
          <div className="filtro-group">
            <label>Al</label>
            <input type="date" value={al} onChange={(e) => setAl(e.target.value)} />
          </div>
          <div className="filtro-group flex-grow">
            <label>Medico</label>
            {riepilogo?.soloProprie ? (
              /* Un medico vede soltanto le proprie: mostrargli un elenco
                 da cui scegliere sarebbe una promessa che il server non
                 mantiene. */
              <p className="sp-solo-proprie">Le tue prestazioni</p>
            ) : (
            <select value={medicoId} onChange={(e) => setMedicoId(e.target.value)}>
              <option value="">Tutti i medici</option>
              {medici.map((m) => (
                <option key={m.id} value={m.id}>
                  Dr. {m.cognome} {m.nome}
                  {m.specializzazione ? ` — ${m.specializzazione}` : ''}
                </option>
              ))}
            </select>
            )}
          </div>
        </div>
        <div className="filtri-shortcuts">
          <button type="button" onClick={setMeseCorrente}>Mese corrente</button>
          <button type="button" onClick={setMesePrecedente}>Mese precedente</button>
          <button type="button" onClick={setAnnoCorrente}>Anno corrente</button>
        </div>
      </div>

      {loading && <div className="spettanze-loading">Calcolo in corso...</div>}
      {error && <div className="error-message">{error}</div>}

      {!loading && riepilogo && (
        <>
          {/* Totali generali */}
          <div className="totali-card">
            <div className="totali-item">
              <span className="label">Visite</span>
              <span className="value">{riepilogo.totali.num_visite}</span>
            </div>
            <div className="totali-item">
              <span className="label">Imponibile</span>
              <span className="value">{formatEuro(riepilogo.totali.imponibile)}</span>
            </div>
            <div className="totali-item">
              <span className="label">Costi prodotto</span>
              <span className="value cost">{formatEuro(riepilogo.totali.costi_prodotto)}</span>
            </div>
            <div className="totali-item">
              <span className="label">Margine</span>
              <span className="value">{formatEuro(riepilogo.totali.margine)}</span>
            </div>
            <div className="totali-item highlight">
              <span className="label">Totale spettanze</span>
              <span className="value">{formatEuro(riepilogo.totali.spettanza)}</span>
              {riepilogo.totali.righe_da_completare > 0 && (
                <span className="senza-percentuale" title="Costo del prodotto da completare: la spettanza non è nel totale finché il lotto non ha il suo costo.">
                  {riepilogo.totali.righe_da_completare} righe da completare
                </span>
              )}
            </div>
            {/* Ritenuta e addebito compaiono solo se ci sono: un riquadro
                a zero non dice nulla e occupa spazio. */}
            {riepilogo.totali.ritenuta > 0 && (
              <div className="totali-item">
                <span className="label">
                  Ritenuta d&apos;acconto
                  {/* Su che cosa è stata calcolata lo dice il server:
                      chi legge un importo trattenuto deve poter sapere
                      su quale imponibile, e la base è un parametro che
                      può cambiare senza toccare il programma. */}
                  {riepilogo.base_ritenuta === 'lordo_meno_ai' && (
                    <em className="base-ritenuta"> sul compenso al netto dell&apos;assistenza</em>
                  )}
                </span>
                <span className="value cost">
                  -{formatEuro(riepilogo.totali.ritenuta)}
                </span>
              </div>
            )}
            {riepilogo.totali.addebito_ai > 0 && (
              <div className="totali-item">
                <span className="label">Assistenza AI</span>
                <span className="value cost">
                  -{formatEuro(riepilogo.totali.addebito_ai)}
                </span>
              </div>
            )}
            {(riepilogo.totali.ritenuta > 0 || riepilogo.totali.addebito_ai > 0) && (
              <div className="totali-item highlight">
                <span className="label">Netto da liquidare</span>
                {/* Il netto arriva dal server, calcolato in centesimi
                    interi. Rifarlo qui con tre sottrazioni in virgola
                    mobile era un quarto posto dove lo stesso numero
                    poteva uscire diverso — e a schermo, dove si legge. */}
                <span className="value">{formatEuro(riepilogo.totali.netto)}</span>
              </div>
            )}
          </div>

          {/* Tabella per medico */}
          {riepilogo.medici.length === 0 ? (
            <div className="no-results">
              Nessuna visita con medico assegnato nel periodo selezionato.
            </div>
          ) : (
            <div className="spettanze-table-wrapper rm-scorre">
              <table className="spettanze-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Medico</th>
                    <th className="text-center">Visite</th>
                    <th className="text-center">Prestazioni</th>
                    <th className="text-right">Imponibile</th>
                    <th className="text-right">Costi</th>
                    <th className="text-right">Margine</th>
                    <th className="text-center">%</th>
                    <th className="text-right spettanza-col">Spettanza</th>
                    <th className="text-right">Ritenuta</th>
                    <th className="text-right">Netto</th>
                  </tr>
                </thead>
                <tbody>
                  {riepilogo.medici.map((m) => {
                    const visiteMed = dettaglioPerMedico[m.medico_id] || [];
                    const isExpanded = expandedMedico === m.medico_id;
                    return (
                      <React.Fragment key={m.medico_id}>
                        <tr className="medico-row">
                          <td>
                            <button
                              className="btn-expand-small"
                              onClick={() => toggleExpand(m.medico_id)}
                              title={isExpanded ? 'Comprimi' : 'Espandi dettaglio'}
                            >
                              {isExpanded ? '▼' : '▶'}
                            </button>
                          </td>
                          <td>
                            <strong>Dr. {m.cognome} {m.nome}</strong>
                            {m.specializzazione && (
                              <div className="specializzazione">{m.specializzazione}</div>
                            )}
                          </td>
                          <td className="text-center">{parseFloat(m.prestazioni).toFixed(0)}</td>
                          <td className="text-center">{parseFloat(m.prestazioni).toFixed(0)}</td>
                          <td className="text-right">{formatEuro(m.imponibile)}</td>
                          <td className="text-right costo">{formatEuro(m.costi_prodotto)}</td>
                          <td className="text-right">{formatEuro(m.margine)}</td>
                          <td className="text-center">
                            {parseFloat(m.percentuale_spettanza).toFixed(0)}%
                            {/* Uno zero per percentuale mancante e uno zero
                                voluto si vedono uguali: qui si distinguono. */}
                            {m.righe_senza_percentuale > 0 && (
                              <div className="senza-percentuale"
                                   title="Righe senza percentuale di spettanza: maturano zero">
                                {m.righe_senza_percentuale} senza %
                              </div>
                            )}
                          </td>
                          <td className="text-right spettanza-col">
                            <strong>{formatEuro(m.spettanza)}</strong>
                            {/* Dal 2027 il costo del prodotto viene dal
                                magazzino: se manca, la riga non vale zero. */}
                            {m.righe_da_completare > 0 && (
                              <div className="senza-percentuale" title="Costo del prodotto da completare: la spettanza non è nel totale finché il lotto non ha il suo costo.">
                                {m.righe_da_completare} da completare
                              </div>
                            )}
                          </td>
                          <td className="text-right costo">
                            {m.ritenuta > 0 ? `-${formatEuro(m.ritenuta)}` : '—'}
                            {m.ritenuta > 0 && (
                              <div className="ritenuta-aliq">{parseFloat(m.ritenuta_aliquota).toFixed(0)}%</div>
                            )}
                          </td>
                          <td className="text-right spettanza-col">
                            <strong>{formatEuro(m.netto)}</strong>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="dettaglio-row">
                            <td colSpan="11">
                              {visiteMed.length === 0 ? (
                                <div className="no-dettaglio">Nessuna visita nel periodo.</div>
                              ) : (
                                <div className="dettaglio-visite">
                                  {visiteMed.map((v) => (
                                    <div key={v.visita_id} className="dettaglio-visita">
                                      <div className="visita-head">
                                        <span className="visita-data-small">{formatDate(v.data_visita)}</span>
                                        <span className="paziente-name">
                                          {v.paziente_cognome} {v.paziente_nome}
                                        </span>
                                        {v.patologia && (
                                          <span className="patologia-small">{v.patologia}</span>
                                        )}
                                        <span className="visita-spett">
                                          Spettanza visita: <strong>{formatEuro(v.spettanza_visita)}</strong>
                                        </span>
                                      </div>
                                      <table className="prest-detail-table">
                                        <thead>
                                          <tr>
                                            <th>Prestazione</th>
                                            <th className="text-right">Prezzo</th>
                                            <th className="text-right">Costo</th>
                                            <th className="text-center">Q.tà</th>
                                            <th className="text-right">Margine riga</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(v.prestazioni || []).length === 0 ? (
                                            <tr>
                                              <td colSpan="5" className="no-prest">
                                                Visita senza prestazioni associate
                                              </td>
                                            </tr>
                                          ) : (
                                            v.prestazioni.map((p) => (
                                              <tr key={p.id}>
                                                <td>{p.nome_snapshot}</td>
                                                <td className="text-right">{formatEuro(p.prezzo_unitario)}</td>
                                                <td className="text-right costo">{formatEuro(p.costo_prodotto_snapshot)}</td>
                                                <td className="text-center">{parseFloat(p.quantita)}</td>
                                                <td className="text-right">{formatEuro(p.margine_riga)}</td>
                                              </tr>
                                            ))
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="formula-info">
            <strong>Formula:</strong> Spettanza = (Prezzo − Costo prodotto) × Quantità × % medico
          </div>
        </>
      )}
    </div>
  );
};

export default Spettanze;
